
import React, { useState } from 'react';
import { Check, Zap, Rocket, Building, ShieldCheck } from 'lucide-react';
import { SubscriptionTier } from '../../types';

interface PricingViewProps {
  onSelectPlan: (plan: SubscriptionTier) => void;
}

export const PricingView: React.FC<PricingViewProps> = ({ onSelectPlan }) => {
  const [isYearly, setIsYearly] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50 py-20 px-6">
      <div className="max-w-6xl mx-auto text-center mb-16">
        <div className="inline-flex items-center space-x-2 px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-xs font-black uppercase mb-4 border border-blue-100">
           <Zap size={14} />
           <span>Scalable API Plans</span>
        </div>
        <h1 className="text-5xl font-black text-slate-900 mb-6 tracking-tighter">Choose Your Compliance Tier</h1>
        <p className="text-slate-500 text-xl max-w-2xl mx-auto font-medium">
          Access real-time intelligence from Opoint, Crystal, and ESPY with the right plan for your volume.
        </p>

        <div className="mt-10 flex items-center justify-center space-x-4">
          <span className={`text-sm font-bold ${!isYearly ? 'text-slate-900' : 'text-slate-400'}`}>Monthly</span>
          <button 
            onClick={() => setIsYearly(!isYearly)}
            className="w-14 h-7 bg-slate-200 rounded-full relative p-1 transition-all"
          >
            <div className={`w-5 h-5 bg-blue-600 rounded-full transition-all shadow-md ${isYearly ? 'translate-x-7' : 'translate-x-0'}`}></div>
          </button>
          <span className={`text-sm font-bold ${isYearly ? 'text-slate-900' : 'text-slate-400'}`}>Yearly <span className="text-emerald-500 text-[10px] uppercase font-black ml-1">-20% Off</span></span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
        <PricingCard 
          icon={<Rocket className="text-slate-400" />}
          name="Free"
          price={0}
          description="Perfect for testing and low-volume individual KYC."
          features={["100 API Calls / Month", "Basic Email & Phone ID", "Standard LEI Registry", "Community Support"]}
          onSelect={() => onSelectPlan('Free')}
        />
        <PricingCard 
          icon={<Zap className="text-blue-500" />}
          name="Pro"
          price={isYearly ? 199 : 249}
          description="The complete intelligence suite for fintechs."
          features={["10k API Calls / Month", "Real-time AML Crypto Monitoring", "Full ESG Scoring (Opoint)", "Market Sentiment (ESPY)", "Priority Support"]}
          popular
          onSelect={() => onSelectPlan('Pro')}
        />
        <PricingCard 
          icon={<Building className="text-indigo-500" />}
          name="Enterprise"
          price={999}
          description="Unlimited compliance for global institutions."
          features={["Unlimited API Calls", "Dedicated Onboarding", "Custom Risk Rules Engine", "Full Audit History", "24/7 Phone Support"]}
          onSelect={() => onSelectPlan('Enterprise')}
        />
      </div>
    </div>
  );
};

const PricingCard: React.FC<{ 
  icon: React.ReactNode, 
  name: string, 
  price: number, 
  description: string, 
  features: string[], 
  popular?: boolean,
  onSelect: () => void 
}> = ({ icon, name, price, description, features, popular, onSelect }) => (
  <div className={`bg-white rounded-3xl p-8 flex flex-col border transition-all hover:shadow-2xl relative ${popular ? 'border-blue-600 shadow-xl shadow-blue-100 scale-105 z-10' : 'border-slate-200'}`}>
    {popular && (
      <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-blue-600 text-white px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest">
        Most Popular
      </div>
    )}
    
    <div className="mb-6">
      <div className="p-3 bg-slate-50 rounded-2xl w-fit mb-4">{icon}</div>
      <h3 className="text-xl font-black text-slate-900 mb-2">{name}</h3>
      <div className="flex items-baseline space-x-1">
        <span className="text-4xl font-black text-slate-900">${price}</span>
        <span className="text-slate-400 font-bold text-sm">/mo</span>
      </div>
      <p className="text-sm text-slate-500 mt-4 font-medium leading-relaxed">{description}</p>
    </div>

    <div className="space-y-4 mb-10 flex-1">
      {features.map((f, i) => (
        <div key={i} className="flex items-center space-x-3">
          <div className="p-0.5 bg-emerald-100 text-emerald-600 rounded-full"><Check size={14} /></div>
          <span className="text-sm font-semibold text-slate-700">{f}</span>
        </div>
      ))}
    </div>

    <button 
      onClick={onSelect}
      className={`w-full py-4 rounded-2xl font-black uppercase tracking-widest text-sm transition-all active:scale-95 ${
      popular 
        ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-200' 
        : 'bg-slate-900 text-white hover:bg-slate-800'
    }`}>
      {price === 0 ? 'Start Free' : 'Get Started'}
    </button>
  </div>
);
